export const SESSION_SNAPSHOT_TRANSPORT_VERSION = 1;

const SESSION_SNAPSHOT_RESPONSE_FIELDS = [
    'snapshotTransport',
    'snapshotRevision',
    'baseSnapshotRevision',
    'snapshotScope',
    'snapshotSectionRevisions',
    'omittedInfoFields'
];

function createScopeIdentity(info) {
    const source = info || {};
    return JSON.stringify([
        source.app_id || null,
        source.business_code || null,
        source.user_code || null,
        source.store_code || null,
        source.employee_code || null,
        source.viewMode || null
    ]);
}

function cleanSessionPayload(session) {
    const result = { ...(session || {}) };
    SESSION_SNAPSHOT_RESPONSE_FIELDS.forEach((field) => delete result[field]);
    return result;
}

function createState(session, info) {
    return {
        snapshotRevision: session.snapshotRevision,
        snapshotScope: session.snapshotScope,
        sectionRevisions: { ...(session.snapshotSectionRevisions || {}) },
        scopeIdentity: createScopeIdentity(info)
    };
}

export function createSessionSnapshotRequest(currentInfo, state, options = {}) {
    const request = { snapshotTransportVersion: SESSION_SNAPSHOT_TRANSPORT_VERSION };
    if (
        options.forceFull ||
        !currentInfo ||
        !state ||
        !state.snapshotRevision ||
        !state.snapshotScope ||
        state.scopeIdentity !== createScopeIdentity(currentInfo)
    )
        return request;

    return {
        ...request,
        knownSnapshotRevision: state.snapshotRevision,
        knownSnapshotScope: state.snapshotScope,
        knownSectionRevisions: { ...(state.sectionRevisions || {}) }
    };
}

/**
 * Rebuild the legacy `{ token, socketToken, info }` contract from a compact
 * refreshToken response. Returns null when the response is based on a stale
 * client revision; callers must retry once with a full snapshot in that case.
 */
export function applySessionSnapshot(currentInfo, state, responseSession) {
    if (!responseSession || !responseSession.token) return null;
    const transport = responseSession.snapshotTransport;
    if (!transport) {
        if (!responseSession.info || typeof responseSession.info !== 'object') return null;
        return {
            session: responseSession,
            state: null,
            infoChanged: true,
            isSnapshot: false
        };
    }

    if (transport === 'session-full-v1') {
        if (!responseSession.info || typeof responseSession.info !== 'object' || !responseSession.snapshotRevision || !responseSession.snapshotScope)
            return null;
        return {
            session: cleanSessionPayload(responseSession),
            state: createState(responseSession, responseSession.info),
            infoChanged: true,
            isSnapshot: true
        };
    }

    const hasCurrentBase = Boolean(
        currentInfo &&
        state &&
        state.snapshotRevision &&
        state.snapshotScope &&
        state.scopeIdentity === createScopeIdentity(currentInfo) &&
        state.snapshotScope === responseSession.snapshotScope
    );
    if (!hasCurrentBase) return null;

    if (transport === 'session-not-modified-v1') {
        if (state.snapshotRevision !== responseSession.snapshotRevision) return null;
        return {
            session: { ...cleanSessionPayload(responseSession), info: currentInfo },
            state: createState(responseSession, currentInfo),
            infoChanged: false,
            isSnapshot: true
        };
    }

    if (transport !== 'session-delta-v1' || responseSession.baseSnapshotRevision !== state.snapshotRevision)
        return null;
    if (!responseSession.info || typeof responseSession.info !== 'object' || !Array.isArray(responseSession.omittedInfoFields))
        return null;

    const responseSections = responseSession.snapshotSectionRevisions || {};
    const nextInfo = { ...responseSession.info };
    for (const field of responseSession.omittedInfoFields) {
        if (
            !Object.prototype.hasOwnProperty.call(currentInfo, field) ||
            !state.sectionRevisions ||
            state.sectionRevisions[field] !== responseSections[field]
        )
            return null;
        nextInfo[field] = currentInfo[field];
    }

    return {
        session: { ...cleanSessionPayload(responseSession), info: nextInfo },
        state: createState(responseSession, nextInfo),
        infoChanged: true,
        isSnapshot: true
    };
}
